import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getPurchaseOrders, getSuppliers, updatePurchaseOrderStatus } from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';
import HelpIcon from '../components/help/HelpIcon';
import { Eye, Check, X } from 'lucide-react';
import './PurchaseOrdersPage.css';

function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [filters, setFilters] = useState({
    supplierId: '',
    status: '',
  });

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersData, suppliersData] = await Promise.all([
        getPurchaseOrders(filters),
        getSuppliers(),
      ]);
      setOrders(ordersData.orders || []);
      setSuppliers(suppliersData.suppliers || []);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find((s) => s.supplierId === supplierId);
    return supplier ? supplier.name : supplierId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'SENT':
        return 'info';
      case 'RECEIVED':
        return 'success';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING':
        return t('purchaseOrders.statusPending', 'Pendiente');
      case 'SENT':
        return t('purchaseOrders.statusSent', 'Enviada');
      case 'RECEIVED':
        return t('purchaseOrders.statusReceived', 'Recibida');
      case 'CANCELLED':
        return t('purchaseOrders.statusCancelled', 'Cancelada');
      default:
        return status;
    }
  };

  const handleStatusChange = async (e, orderId, newStatus) => {
    e.stopPropagation();
    const statusLabel = getStatusLabel(newStatus);
    if (
      !window.confirm(
        t('purchaseOrders.confirmStatusChange', '¿Cambiar estado a {{status}}?', {
          status: statusLabel,
        })
      )
    )
      return;
    try {
      await updatePurchaseOrderStatus(orderId, newStatus);
      await loadData();
      showFeedback(
        'success',
        t('purchaseOrders.statusUpdateSuccess', 'Estado actualizado exitosamente')
      );
    } catch (error) {
      console.error('Error updating status:', error);
      showFeedback('error', t('purchaseOrders.statusUpdateError', 'Error al actualizar estado'));
    }
  };

  return (
    <div className="purchase-orders-page">
      <div className="page-header">
        <h1>
          {t('purchaseOrders.title', 'Órdenes de Compra')}{' '}
          <HelpIcon topicKey="purchaseOrders.overview" />
        </h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/inventory/purchase-orders/new')}
        >
          {t('purchaseOrders.create', 'Nueva Orden')}
        </button>
      </div>

      <div className="filters-bar">
        <select
          value={filters.supplierId}
          onChange={(e) => setFilters({ ...filters, supplierId: e.target.value })}
        >
          <option value="">{t('common.allSuppliers', 'Todos los proveedores')}</option>
          {suppliers.map((s) => (
            <option key={s.supplierId} value={s.supplierId}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">{t('common.allStatuses', 'Todos los estados')}</option>
          <option value="PENDING">{getStatusLabel('PENDING')}</option>
          <option value="SENT">{getStatusLabel('SENT')}</option>
          <option value="RECEIVED">{getStatusLabel('RECEIVED')}</option>
          <option value="CANCELLED">{getStatusLabel('CANCELLED')}</option>
        </select>
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      <div className="card">
        <div className="table-responsive">
          <table className="purchase-orders-table">
            <thead>
              <tr>
                <th>{t('purchaseOrders.creationDate', 'Fecha Creación')}</th>
                <th>{t('purchaseOrders.expectedDate', 'Fecha Esperada')}</th>
                <th>{t('suppliers.supplier', 'Proveedor')}</th>
                <th>{t('common.total', 'Total')}</th>
                <th>
                  {t('common.status', 'Estado')} <HelpIcon topicKey="purchaseOrders.statusFlow" />
                </th>
                <th>
                  {t('common.items', 'Items')} <HelpIcon topicKey="purchaseOrders.stockImpact" />
                </th>
                <th>{t('common.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    {loading ? t('common.loading') : t('common.noData')}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order.orderId}
                    onClick={() => navigate(`/inventory/purchase-orders/${order.orderId}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td data-label={t('purchaseOrders.creationDate', 'Fecha Creación')}>
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td data-label={t('purchaseOrders.expectedDate', 'Fecha Esperada')}>
                      {order.expectedDate
                        ? new Date(order.expectedDate).toLocaleDateString(undefined, {
                            timeZone: 'UTC',
                          })
                        : '—'}
                    </td>
                    <td data-label={t('suppliers.supplier', 'Proveedor')}>
                      {getSupplierName(order.supplierId)}
                    </td>
                    <td data-label={t('common.total', 'Total')}>{formatCurrency(order.total)}</td>
                    <td data-label={t('common.status', 'Estado')}>
                      <span className={`badge badge-${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td data-label={t('common.items', 'Items')}>{order.items.length}</td>
                    <td data-label={t('common.actions', 'Acciones')}>
                      <div className="actions-cell">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title={t('purchaseOrders.viewDetail', 'Ver detalle')}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/inventory/purchase-orders/${order.orderId}`);
                          }}
                        >
                          <Eye size={16} />
                        </button>
                        {(order.status === 'PENDING' || order.status === 'SENT') && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            title={t('purchaseOrders.markAsReceived', 'Marcar como Recibida')}
                            onClick={(e) => handleStatusChange(e, order.orderId, 'RECEIVED')}
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {(order.status === 'PENDING' || order.status === 'SENT') && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            title={t('common.cancelOrder', 'Cancelar Orden')}
                            onClick={(e) => handleStatusChange(e, order.orderId, 'CANCELLED')}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PurchaseOrdersPage;
